import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { transformDbActivityToTimeline } from "@/lib/dataTransformers";
import { useBranchContext } from "@/hooks/useBranchContext";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";

// Helper function to group parallel activities by parallel_group_id
const groupParallelActivities = (activities: any[]): any[] => {
  const grouped: any[] = [];
  const parallelGroups = new Map<string, any[]>();
  
  activities.forEach(activity => {
    if (activity.is_parallel_child && activity.parallel_group_id) {
      if (!parallelGroups.has(activity.parallel_group_id)) {
        parallelGroups.set(activity.parallel_group_id, []);
      }
      parallelGroups.get(activity.parallel_group_id)!.push(activity);
    } else if (activity.status !== 'deprecated') {
      grouped.push(activity);
    }
  });
  
  parallelGroups.forEach((children, groupId) => {
    if (children.length === 0) return;
    
    const workflowOrder = ['DSS', 'DSF', 'PC_PHARMACY', 'CS'];
    const orderedChildren = workflowOrder
      .map(step => children.find(c => c.workflow_step === step))
      .filter(Boolean);
    
    const allCompleted = orderedChildren.every(c => c.status === 'completed');
    const hasInProgress = orderedChildren.some(c => c.status === 'in-progress');
    const groupStatus = allCompleted ? 'completed' : hasInProgress ? 'in-progress' : 'pending';
    
    grouped.push({
      id: groupId,
      activity_id: groupId,
      name: 'Discharge Documentation & Clearances',
      description: 'Parallel activities for discharge process',
      status: groupStatus,
      is_parallel_group: true,
      parallel_activities: orderedChildren,
      display_order: orderedChildren[0].display_order,
      patient_id: children[0].patient_id,
      workflow_step: 'DISCHARGE_CLEARANCE_GROUP',
      is_standard: false,
      created_at: orderedChildren[0].created_at,
      updated_at: Math.max(...orderedChildren.map(c => new Date(c.updated_at).getTime())),
    });
  });
  
  return grouped.sort((a, b) => a.display_order - b.display_order);
};

export const useActivities = (patientId: string | null) => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  
  return useQuery({
    queryKey: ["activities", organizationId, currentBranchId, patientId],
    queryFn: async () => {
      if (!patientId || currentBranchId == null || !organizationId) return [];

      const { data, error } = await supabase
        .from("activities")
        .select("*")
        .eq("patient_id", patientId)
        .eq("branch_id", currentBranchId)
        .eq("organization_id", organizationId)
        .neq("status", "deprecated")
        .order("display_order")
        .order("workflow_step", { ascending: true });

      if (error) throw error;
      
      const rawActivities = data || [];
      const mdCompletionTime = rawActivities.reduce<Date | null>((latest, activity) => {
        if (activity?.workflow_step !== 'MD') return latest;
        const ts = activity?.completion_time || activity?.timestamp;
        if (!ts) return latest;
        const parsed = new Date(ts);
        if (isNaN(parsed.getTime())) return latest;
        if (!latest || parsed.getTime() > latest.getTime()) return parsed;
        return latest;
      }, null);

      const applyDssFallback = (activity: any) => {
        if (!activity) return activity;
        if (
          activity.workflow_step === 'DSS' &&
          activity.status === 'completed' &&
          !activity.time_taken_minutes &&
          activity.completion_time &&
          mdCompletionTime
        ) {
          const completion = new Date(activity.completion_time);
          if (!isNaN(completion.getTime())) {
            const diffMs = completion.getTime() - mdCompletionTime.getTime();
            const diffMinutes = Math.floor(diffMs / (1000 * 60));
            if (diffMinutes > 0) return { ...activity, time_taken_minutes: diffMinutes };
          }
        }
        return activity;
      };

      const groupedActivities = groupParallelActivities(rawActivities).map((activity) => {
        if (activity?.is_parallel_group && Array.isArray(activity.parallel_activities)) {
          return { ...activity, parallel_activities: activity.parallel_activities.map(applyDssFallback) };
        }
        return applyDssFallback(activity);
      });
      
      return groupedActivities.map((activity) => {
        const staffName = activity.doctor;
        let processedActivity = { ...activity, staff_name: staffName };
        
        if (activity.is_parallel_group && activity.parallel_activities && Array.isArray(activity.parallel_activities)) {
          processedActivity.parallel_activities = activity.parallel_activities.map((nestedActivity: any) => ({
            ...nestedActivity,
            staff_name: nestedActivity.doctor,
          }));
        }
        
        return transformDbActivityToTimeline(processedActivity);
      });
    },
    enabled: !!patientId && currentBranchId != null && !!organizationId,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
};
